
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import { EventBus } from '@/main';
@Component({})
export default class DataImporting extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;

  @Prop({ default: false })
  public isAthenaFinished!: boolean;

  @Prop({ default: false })
  public isAthenaReady!: boolean;

  @Prop({ default: null })
  public app!: any;

  /**
   * Calculate progress for a resource based on ETL plan ranges and current ETL startTime.
   * Ranges are ordered from most recent to oldest. The ETL processes backwards in time.
   * A range is complete when etlStatus.startTime <= range.startTime.
   * Returns { completed, total, percent }.
   */
  private calculateRangeCounts(
    ranges: Array<{ startTime: string; endTime: string; apiCount: number }>,
    etlResource: string,
    etlType: string,
  ): { completed: number; total: number; percent: number } {
    const total = ranges.reduce((sum, r) => sum + (r.apiCount || 0), 0);
    if (!ranges || ranges.length === 0 || total === 0) {
      return { completed: 0, total, percent: 0 };
    }

    // Find the current ETL startTime for this resource
    let currentEtlStartTime: number | null = null;
    for (const etlStatus of this.store.etlStatuses.items) {
      if (etlStatus.resource === etlResource && etlStatus.etlType === etlType) {
        currentEtlStartTime = new Date(etlStatus.startTime).setHours(0, 0, 0, 0);
        break;
      }
    }

    if (currentEtlStartTime === null) {
      return { completed: 0, total, percent: 0 };
    }

    let completed = 0;
    for (const range of ranges) {
      const rangeStart = new Date(range.startTime).setHours(0, 0, 0, 0);
      const rangeEnd = new Date(range.endTime).setHours(0, 0, 0, 0);

      if (currentEtlStartTime <= rangeStart) {
        // Range fully complete
        completed += range.apiCount || 0;
      } else if (currentEtlStartTime < rangeEnd) {
        // Partially complete — interpolate by time proportion
        const totalSpan = rangeEnd - rangeStart;
        const completedSpan = rangeEnd - currentEtlStartTime;
        if (totalSpan > 0) {
          completed += (completedSpan / totalSpan) * (range.apiCount || 0);
        }
      }
    }

    completed = Math.min(Math.round(completed), total);
    return { completed, total, percent: Math.min((completed / total) * 100, 100) };
  }

  public get orderRangeCounts() {
    const plan = this.store.currentPackage?.etlPlanOrders;
    if (plan && plan.length > 0) {
      return this.calculateRangeCounts(plan, 'orders', 'etl_data_period');
    }
    return null;
  }

  public get productRangeCounts() {
    const plan = this.store.currentPackage?.etlPlanProducts;
    if (plan && plan.length > 0) {
      return this.calculateRangeCounts(plan, 'products', 'etl_data_period');
    }
    return null;
  }

  public get orderProgress(): number {
    // If resource count confirms done, return 100% immediately
    if (this.isResourceDone(this.store.orderCount)) {
      return 100;
    }
    if (this.orderRangeCounts) {
      return this.orderRangeCounts.percent;
    }
    if (!this.store.orderCount || this.store.orderCount.apiCount <= 0) {
      return 0;
    }
    return (this.store.orderCount.modelCount / this.store.orderCount.apiCount) * 100;
  }

  public get productProgress(): number {
    if (this.isResourceDone(this.store.productCount)) {
      return 100;
    }
    if (this.productRangeCounts) {
      return this.productRangeCounts.percent;
    }
    if (!this.store.productCount || this.store.productCount.apiCount <= 0) {
      return 0;
    }
    return (this.store.productCount.modelCount / this.store.productCount.apiCount) * 100;
  }

  private resourceProgress(count: { modelCount: number; apiCount: number; resource?: string } | null): number {
    if (!count) return 0;
    // resource is '' before API response — not loaded yet
    if (!count.resource) return 0;
    if (count.apiCount <= 0) return 100;
    return Math.min((count.modelCount / count.apiCount) * 100, 100);
  }

  /**
   * Progress from sales history "ready" (1 year) to "finished" (full history).
   * Uses ETL startTime position between readyStartTime and limitStartTime.
   */
  public get salesFullHistoryProgress(): number {
    if (this.store.isSalesOrderOlapFinished) return 100;
    if (!this.store.isSalesOrderOlapReady) return 0;
    // Interpolate between ready (1 year) and finished (full history)
    const range = this.salesHistoryRange;
    if (!range || !range.startTime || !range.limitStartTime) return 0;
    const installedAt = new Date(this.store.installedAt).setHours(0, 0, 0, 0);
    let readyStartTime = new Date(installedAt);
    readyStartTime.setFullYear(readyStartTime.getFullYear() - 1);
    const readyTs = readyStartTime.getTime();
    const limitTs = range.limitStartTime.getTime();
    const currentTs = range.startTime.getTime();
    if (readyTs <= limitTs) return 100; // 1 year already covers full history
    const totalSpan = readyTs - limitTs;
    const completedSpan = readyTs - currentTs;
    return Math.min(Math.max((completedSpan / totalSpan) * 100, 0), 100);
  }

  public get hasMoreThanOneYearHistory(): boolean {
    if (!this.store.firstOrderAt || !this.store.installedAt) return false;
    const firstOrderTime = new Date(this.store.firstOrderAt).getTime();
    const installedAt = new Date(this.store.installedAt).setHours(0, 0, 0, 0);
    const oneYearAgo = new Date(installedAt);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return firstOrderTime < oneYearAgo.getTime();
  }

  public get overallProgress(): number {
    const locationProg = this.resourceProgress(this.store.locationCount);
    const collectionProg = this.resourceProgress(this.collectionsCount);
    const productProg = this.resourceProgress(this.store.productCount);
    const orderProg = this.orderProgress;
    const rawImportProgress = (locationProg + collectionProg + productProg + orderProg) / 4;
    const salesReady = this.store.isSalesOrderOlapReady ? 100 : 0;
    const salesFinished = this.salesFullHistoryProgress;

    if (this.isFreePlan) {
      // Free plan: no inventory, only 6 months sales — raw imports 70%, sales ready 30%
      return (rawImportProgress * 0.7) + (salesReady / 100 * 30);
    }
    // Paid plan: raw imports 60%, sales 30% total, inventory 10%
    const inventory = this.store.isInventoryLevelsReady ? 100 : 0;
    if (this.hasMoreThanOneYearHistory) {
      return (rawImportProgress * 0.6) + (salesReady / 100 * 20) + (salesFinished / 100 * 10) + (inventory / 100 * 10);
    }
    return (rawImportProgress * 0.6) + (salesReady / 100 * 30) + (inventory / 100 * 10);
  }

  public get estimatedMinutes(): number {
    return this.store.currentPackage?.predictEtlMinutes ?? -1;
  }

  public get estimatedTime(): string {
    if (this.store.currentPackage && this.store.currentPackage.predictEtlTime) {
      return this.store.currentPackage.predictEtlTime;
    }
    return 'Calculating...';
  }

  public get salesHistoryRange() {
    for (const etlStatus of this.store.etlStatuses.items) {
      if (etlStatus.resource === 'sales_order_variant_olap') {
        const startTime = etlStatus.startTime ? new Date(etlStatus.startTime) : null;
        const endTime = etlStatus.endTime ? new Date(etlStatus.endTime) : null;
        const limitStartTime = etlStatus.limitStartTime ? new Date(etlStatus.limitStartTime) : null;
        return { startTime, endTime, limitStartTime };
      }
    }
    return null;
  }

  public get salesReadySubtitleFree(): string {
    const range = this.salesHistoryRange;
    if (!range || !range.startTime || !range.endTime) return 'Waiting to start...';
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    if (this.store.isSalesOrderOlapReady) {
      const installedAt = new Date(this.store.installedAt).setHours(0, 0, 0, 0);
      const sixMonthsAgo = new Date(installedAt);
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      return `${fmt(sixMonthsAgo)} — ${fmt(range.endTime)}`;
    }
    return `Processed to ${fmt(range.startTime)}`;
  }

  public get salesReadySubtitle(): string {
    const range = this.salesHistoryRange;
    if (!range || !range.startTime || !range.endTime) return 'Waiting to start...';
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    if (this.store.isSalesOrderOlapReady) {
      const installedAt = new Date(this.store.installedAt).setHours(0, 0, 0, 0);
      const readyDate = new Date(installedAt);
      readyDate.setFullYear(readyDate.getFullYear() - 1);
      return `${fmt(readyDate)} — ${fmt(range.endTime)}`;
    }
    return `Processed to ${fmt(range.startTime)}`;
  }

  public get salesFinishedSubtitle(): string {
    const range = this.salesHistoryRange;
    if (!range || !range.limitStartTime) return 'Waiting...';
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const installedAt = new Date(this.store.installedAt).setHours(0, 0, 0, 0);
    const oneYearAgo = new Date(installedAt);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    if (this.store.isSalesOrderOlapFinished) {
      return `${fmt(range.limitStartTime)} — ${fmt(oneYearAgo)}`;
    }
    if (this.store.isSalesOrderOlapReady) {
      return `Importing... ${Math.round(this.salesFullHistoryProgress)}%`;
    }
    return 'Waiting for recent history first...';
  }

  private isResourceDone(count: { modelCount: number; apiCount: number; resource?: string } | null): boolean {
    if (!count) return false;
    // resource is '' before API response — not loaded yet
    if (!count.resource) return false;
    if (count.apiCount <= 0) return true;
    return count.modelCount >= count.apiCount;
  }

  private resourceSubtitle(count: { modelCount: number; apiCount: number } | null, label: string): string {
    if (!count) return `Importing ${label}...`;
    return `${this.formatNumber(count.modelCount)} / ${this.formatNumber(count.apiCount)}`;
  }

  public get collectionsCount() {
    const customApi = this.store.customCollection?.apiCount || 0;
    const smartApi = this.store.smartCollection?.apiCount || 0;
    const customModel = this.store.customCollection?.modelCount || 0;
    const smartModel = this.store.smartCollection?.modelCount || 0;
    const apiCount = customApi + smartApi;
    const modelCount = Math.min(customModel + smartModel, apiCount);
    const loaded = !!(this.store.customCollection?.resource || this.store.smartCollection?.resource);
    return { modelCount, apiCount, resource: loaded ? 'collections' : '' };
  }

  public get readinessItems() {
    const items = [
      {
        key: 'locations',
        title: 'Locations Imported',
        subtitle: this.resourceSubtitle(this.store.locationCount, 'locations'),
        done: this.isResourceDone(this.store.locationCount),
      },
      {
        key: 'collections',
        title: 'Collections Imported',
        subtitle: this.resourceSubtitle(this.collectionsCount, 'collections'),
        done: this.isResourceDone(this.collectionsCount),
      },
      {
        key: 'products',
        title: 'Products Imported',
        subtitle: this.resourceSubtitle(this.store.productCount, 'products'),
        done: this.isResourceDone(this.store.productCount),
      },
      {
        key: 'orders',
        title: 'Orders Imported',
        subtitle: this.orderRangeCounts
          ? `${this.formatNumber(this.orderRangeCounts.completed)} / ${this.formatNumber(this.orderRangeCounts.total)}`
          : this.resourceSubtitle(this.store.orderCount, 'orders'),
        done: this.orderRangeCounts ? this.orderRangeCounts.percent >= 100 : this.isResourceDone(this.store.orderCount),
      },
    ];
    if (!this.isFreePlan) {
      items.push({
        key: 'inventory',
        title: 'Inventory Levels Aggregated',
        subtitle: this.store.isInventoryLevelsReady ? '' : 'Aggregating inventory data...',
        done: this.store.isInventoryLevelsReady,
      });
    }
    if (this.isFreePlan) {
      items.push({
        key: 'salesOrderReady',
        title: 'Sales history aggregation (last 6 months)',
        subtitle: this.store.isSalesOrderOlapReady ? '' : this.salesReadySubtitleFree,
        done: this.store.isSalesOrderOlapReady,
      });
    } else {
      items.push({
        key: 'salesOrderReady',
        title: 'Sales history aggregation (last 1 year)',
        subtitle: this.store.isSalesOrderOlapReady ? '' : this.salesReadySubtitle,
        done: this.store.isSalesOrderOlapReady,
      });
      if (this.hasMoreThanOneYearHistory) {
        items.push({
          key: 'salesOrderFinished',
          title: 'Sales history aggregation (older than 1 year)',
          subtitle: this.store.isSalesOrderOlapFinished ? '' : this.salesFinishedSubtitle,
          done: this.store.isSalesOrderOlapFinished,
        });
      }
    }
    return items;
  }

  public get readinessCount(): number {
    return this.readinessItems.filter((item) => item.done).length;
  }

  public get isFreePlan(): boolean {
    return this.store.currentPackage?.price === 0;
  }

  public get isProfessionalPlan(): boolean {
    if (!this.store.currentPackage || !this.store.currentPackage.name) {
      return false;
    }
    return this.store.currentPackage.name.toLowerCase().includes('professional');
  }

  public get isRCPlan(): boolean {
    if (!this.store.currentPackage || !this.store.currentPackage.name) {
      return false;
    }
    const name = this.store.currentPackage.name.toLowerCase();
    return name.includes('rc') || name.includes('replenishment');
  }

  public get aiAgents() {
    const agents = [
      {
        id: 'report-builder',
        title: 'AI Report Builder',
        description: 'Build any report by describing what you need in plain English',
        icon: 'mdi-chart-box-outline',
        hexColor: '#59C8E4',
        bgColor: '#e3f2fd',
        examplePrompt: 'Show me top 10 products by revenue last month',
        btnIcon: 'mdi-chat-outline',
        btnLabel: 'Start Building',
        priority: this.isProfessionalPlan ? 0 : 1,
        route: '/assistant/ai-report-builder',
      },
      {
        id: 'onboarding',
        title: 'Onboarding Assistant',
        description: 'Get guided through setting up Assisty step by step',
        icon: 'mdi-rocket-launch-outline',
        hexColor: '#faaf3b',
        bgColor: '#fff3e0',
        examplePrompt: 'Help me get started with Assisty',
        btnIcon: 'mdi-play-circle-outline',
        btnLabel: 'Start Onboarding',
        priority: this.isRCPlan ? 0 : 1,
        route: '/assistant/onboarding',
      },
      {
        id: 'customer-service',
        title: 'Ask Me Anything',
        description: 'Your personal AI expert for all things Assisty and e-commerce',
        icon: 'mdi-message-text-outline',
        hexColor: '#99be50',
        bgColor: '#e8f5e9',
        examplePrompt: 'How do I track stockout rates?',
        btnIcon: 'mdi-chat-outline',
        btnLabel: 'Ask Now',
        priority: 2,
      },
    ];
    return agents.sort((a, b) => a.priority - b.priority);
  }

  public loadingCounts: boolean = false;
  private fastPollingInterval: number | null = null;
  private slowPollingInterval: number | null = null;
  public displayedProgress: number = 0;
  private progressAnimFrame: number | null = null;

  @Watch('overallProgress')
  public onOverallProgressChange(target: number) {
    this.animateProgress(target);
  }

  private animateProgress(target: number) {
    if (this.progressAnimFrame) {
      cancelAnimationFrame(this.progressAnimFrame);
    }
    const step = () => {
      const diff = target - this.displayedProgress;
      if (Math.abs(diff) < 0.5) {
        this.displayedProgress = target;
        this.progressAnimFrame = null;
        return;
      }
      // Ease toward target: move 8% of remaining distance per frame
      this.displayedProgress += diff * 0.08;
      this.progressAnimFrame = requestAnimationFrame(step);
    };
    this.progressAnimFrame = requestAnimationFrame(step);
  }

  public get storeName(): string {
    return this.store.information?.name || this.store.shopName?.split('.')[0] || '';
  }

  public get isDebugMode(): boolean {
    return this.$route.query.debug === 'true';
  }

  public async created() {
    // Initial load: fetch all counts once to know which are done
    await this.loadAllCounts();
    this.animateProgress(this.overallProgress);
    if (!this.isAthenaFinished) {
      this.startCountPolling();
    }
    EventBus.$emit('etl-polling-started');
  }

  public beforeDestroy() {
    this.stopCountPolling();
    if (this.progressAnimFrame) {
      cancelAnimationFrame(this.progressAnimFrame);
    }
    EventBus.$emit('etl-polling-stopped');
  }

  @Watch('isAthenaFinished')
  public onAthenaFinished(val: boolean) {
    if (val) {
      this.stopCountPolling();
    }
  }

  public get allImportsDone(): boolean {
    return this.isResourceDone(this.store.locationCount)
      && this.isResourceDone(this.collectionsCount)
      && this.isResourceDone(this.store.productCount)
      && (this.orderRangeCounts ? this.orderRangeCounts.percent >= 100 : this.isResourceDone(this.store.orderCount));
  }

  private async loadAllCounts() {
    this.loadingCounts = true;
    try {
      await Promise.all([
        this.store.currentPackage.getCurrent(),
        this.store.getStoreData(),
        this.store.resourceCount('locations'),
        this.store.resourceCount('custom_collections'),
        this.store.resourceCount('smart_collections'),
        this.store.resourceCount('products'),
      ]);
    } catch (e) {
      // silently fail
    }
    this.loadingCounts = false;
  }

  // Locations & collections — every 30s
  private async pollFastCounts() {
    const calls: Promise<any>[] = [];
    if (!this.isResourceDone(this.store.locationCount)) {
      calls.push(this.store.resourceCount('locations'));
    }
    if (!this.isResourceDone(this.store.customCollection)) {
      calls.push(this.store.resourceCount('custom_collections'));
    }
    if (!this.isResourceDone(this.store.smartCollection)) {
      calls.push(this.store.resourceCount('smart_collections'));
    }
    if (calls.length === 0) {
      // All fast resources done, stop this interval
      if (this.fastPollingInterval) {
        window.clearInterval(this.fastPollingInterval);
        this.fastPollingInterval = null;
      }
      return;
    }
    try {
      await Promise.all(calls);
    } catch (e) {
      // silently fail
    }
  }

  // Products, orders, sales & inventory — every 60s
  private async pollSlowCounts() {
    this.loadingCounts = true;
    try {
      const calls: Promise<any>[] = [
        this.store.getStoreData(),
      ];
      if (!this.isResourceDone(this.store.productCount)) {
        calls.push(this.store.resourceCount('products'));
      }
      await Promise.all(calls);
    } catch (e) {
      // silently fail
    }
    this.loadingCounts = false;
    if (this.isAthenaFinished) {
      this.stopCountPolling();
    }
  }

  private startCountPolling() {
    this.fastPollingInterval = window.setInterval(() => {
      this.pollFastCounts();
    }, 30000);
    this.slowPollingInterval = window.setInterval(() => {
      this.pollSlowCounts();
    }, 60000);
  }

  private stopCountPolling() {
    if (this.fastPollingInterval) {
      window.clearInterval(this.fastPollingInterval);
      this.fastPollingInterval = null;
    }
    if (this.slowPollingInterval) {
      window.clearInterval(this.slowPollingInterval);
      this.slowPollingInterval = null;
    }
  }

  public formatNumber(value: number): string {
    return value.toLocaleString('en-US');
  }

  public openAIAgent(agentId: string) {
    EventBus.$emit('chatbox:open-and-select-agent', agentId);
  }

  public goToDashboard() {
    this.$router.push('/');
  }

  public openDemoStore() {
    window.open('https://demo.assisty.ai', '_blank');
  }

  public bookDemo() {
    const storeName = this.store.shopName || '';
    window.open(`https://calendly.com/assisty/30min?a1=${encodeURIComponent(storeName)}`, '_blank');
  }
}
